import { useMemo } from 'react';
import { useApiGet, useApiPost, MutationOptions, PostRequestParams } from '../../../services/tanstackApiService';
import * as Yup from 'yup';

export type PreviousTariffStatus = 'Active' | 'In-Active' | 'Expired';

export interface NewTariffRow {
  id: string;
  tariff: string;
  source: string;
}

export interface PreviousTariffRow {
  id: string;
  tariff: string;
  source: string;
  effectiveDate: string;
  expiryDate: string;
  status: PreviousTariffStatus;
}

// default rows for "Previous Tariff" section
export const defaultPreviousTariffs: PreviousTariffRow[] = [
  {
    id: 'p1',
    tariff: 'Bhubaneswar Standard',
    source: 'Walk-In',
    effectiveDate: '12/12/2025',
    expiryDate: '12/12/2026',
    status: 'Active',
  },
  {
    id: 'p2',
    tariff: 'Bhubaneswar Standard',
    source: 'Walk-In',
    effectiveDate: '12/12/2025',
    expiryDate: '12/12/2026',
    status: 'In-Active',
  },
  {
    id: 'p3',
    tariff: 'Insurance – HDFC Ergo',
    source: 'Insurance',
    effectiveDate: '12/12/2025',
    expiryDate: '12/10/2025',
    status: 'Expired',
  },
];

// ---------------------------------------------------------------------------
// Previous Tariff list – for "Previous Tariff" table
// ---------------------------------------------------------------------------

/**
 * Actual API shape from /patient-tariff/?patient_id=...
 * {
 *   "success": true,
 *   "data": [
 *     {
 *       "id": 3,
 *       "patient": 1718,
 *       "tariff": 3,
 *       "tariff_label": "Womens day Special",
 *       "source": 3,
 *       "source_label": "Insurance",
 *       "assigned_date": "2025-12-03",
 *       "status": "ACTIVE",
 *       "created_on": "2025-12-03T12:26:47.306081Z"
 *     }
 *   ]
 * }
 */
export interface PreviousTariffApiItem {
  id: number;
  patient: number;
  tariff: number;
  tariff_label: string;
  source: number;
  source_label: string;
  assigned_date: string;
  status: 'ACTIVE' | 'IN_ACTIVE' | 'EXPIRED';
  created_on: string;
  tariff_end_date?: string;
}

interface PreviousTariffApiResponse {
  success: boolean;
  data: PreviousTariffApiItem[];
}

/**
 * Hook to fetch previous tariffs for a patient using shared tanstackApiService
 * GET /patient-tariff/?patient_id=...
 */
export function usePreviousTariffs(patientId?: number | null) {
  return useApiGet<PreviousTariffApiResponse>(
    ['patient-tariff', 'previous', patientId ?? 0],
    {
      path: '/patient-tariff/',       // <- apiRequest baseUrl + this path
      query: patientId ? { patient_id: patientId } : undefined, // <- goes into options.query
    },
    {
      enabled: !!patientId,           // <- passed directly to useQuery
    }
  );
}

/**
 * Returns raw API items for UI to show directly.
 */
export function usePreviousTariffsRows(patientId?: number | null) {
  const { data, isLoading, isError, refetch } = usePreviousTariffs(patientId);

  const items: PreviousTariffApiItem[] = useMemo(
    () => data?.data ?? [],
    [data]
  );

  return {
    items,
    isLoading,
    isError,
    refetch,
  };
}

// ---------------------------------------------------------------------------
// Patient Source list (for Source dropdown)
// ---------------------------------------------------------------------------

// This is one item from the API response.
export interface PatientSource {
  id: number;
  name: string;
  code: string;
  description: string;
}

// This is the full API response shape:
// { success: true, data: PatientSource[] }
interface PatientSourcesResponse {
  success: boolean;
  data: PatientSource[];
}

// Call GET /patient-tariff/patient-sources/ via useApiGet
export function usePatientSources() {
  return useApiGet<PatientSourcesResponse>(
    ['patient-tariff', 'patient-sources'],
    { path: '/patient-tariff/patient-sources/' },
    {
      // cache for 5 minutes
      staleTime: 5 * 60 * 1000,
    }
  );
}

// Shape used by the <Select> component.
export interface PatientSourceOption {
  label: string; // what user sees
  value: number; // send id to backend
}

// Convert API item -> dropdown option.
export function mapPatientSourceToOption(
  item: PatientSource
): PatientSourceOption {
  return {
    label: item.name,
    value: item.id,
  };
}

// Final hook used by UI for the Source dropdown.
export function usePatientSourcesOptions() {
  const { data, isLoading, isError } = usePatientSources();

  // data?.data is the array of PatientSource items.
  const options: PatientSourceOption[] = useMemo(
    () => (data?.data ?? []).map(mapPatientSourceToOption),
    [data]
  );

  return {
    options,
    isLoading,
    isError,
  };
}

// ---------------------------------------------------------------------------
// Tariff list (for Tariff dropdown)
// ---------------------------------------------------------------------------

/**
 * One Tariff record as returned by backend API.
 *
 * Example JSON (inside data array):
 * {
 *   "id": 1,
 *   "name": "Bhubaneswar Standard",
 *   "code": "BHUB_STD",
 *   "description": "Default tariff for Bhubaneswar"
 * }
 */
export interface Tariff {
  id: number;
  name: string;
  code: string;
  description?: string;
  // Optional fields from API (added for display purposes)
  start_date?: string | null;
  end_date?: string | null;
  status?: boolean;
}

/**
 * Full API response shape for GET /tariff/
 *
 * {
 *   "success": true,
 *   "data": [ { ...Tariff }, { ...Tariff }, ... ]
 * }
 */
interface TariffResponse {
  success: boolean;
  data: Tariff[];
}

/**
 * Example of how to call a GET API using our shared tanstackApiService.
 *
 * This wraps:
 *   GET {baseUrl}/tariff/
 *
 * Key points (freshers can copy this pattern for other APIs):
 *
 * 1. `useApiGet<TResponse>()`
 *    - TResponse is the full response type (here: TariffResponse).
 *
 * 2. First argument: `queryKey`
 *    - Any array that uniquely identifies this query.
 *    - Example: ['tariff', 'list'] or ['patients', clinicId].
 *
 * 3. Second argument: request config
 *    - `path`: API path after baseUrl (no need to include baseUrl).
 *      Here we call `/tariff/`.
 *    - `query`: optional object for query parameters (e.g. { page: 1 }).
 *
 * 4. Third argument: react-query options (optional)
 *    - Example: `staleTime`, `enabled`, `cacheTime`, etc.
 *
 * To create a new GET hook for another endpoint:
 *   - Define a response interface (like TariffResponse).
 *   - Call `useApiGet<ThatResponse>` with your own queryKey + path.
 */
export function useTariffs() {
  return useApiGet<TariffResponse>(
    // queryKey: used by React Query for caching and refetching
    ['tariff', 'list'],
    // request config: path and optional query params
    { path: '/tariff/' },
    // react-query options: here we cache data for 5 minutes
    {
      staleTime: 5 * 60 * 1000,
    }
  );
}

/**
 * Final hook used by UI for the Tariff dropdown.
 * It simplifies the raw response to just `Tariff[]`.
 *
 * Usage example:
 *   const { tariffs, isLoading, isError } = useTariffOptions();
 */
export function useTariffOptions() {
  const { data, isLoading, isError } = useTariffs();

  // data?.data is the array of Tariff items from backend
  const tariffs: Tariff[] = useMemo(
    () => data?.data ?? [],
    [data]
  );

  return {
    tariffs,
    isLoading,
    isError,
  };
}


/**
 * ---------------------------------------------------------------------------
 * Billing / Patient Tariff – Assign tariff to patient
 * ---------------------------------------------------------------------------
 */

export interface AssignPatientTariffItem {
  patient: number;
  tariff: number;
  source: number;
  assigned_date: string; // 'YYYY-MM-DD'
  status: string;        // 'ACTIVE'
}

export interface AssignPatientTariffRequest {
  items: AssignPatientTariffItem[];
}

export interface AssignPatientTariffResponse {
  success: boolean;
  message?: string; // e.g. "Tariff assigned successfully."
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
}

/**
 * Typed wrapper around useApiPost for POST /patient-tariff/
 *
 * mutate usage:
 *   const assignTariff = useAssignPatientTariff();
 *   assignTariff.mutate({
 *     path: '/patient-tariff/',
 *     body: { patient: 1, tariff: 1, source: 1, assigned_date: '2025-01-01', status: 'ACTIVE' },
 *   });
 */
export function useAssignPatientTariff(
  options?: MutationOptions<
    AssignPatientTariffResponse,
    PostRequestParams<AssignPatientTariffRequest>
  >
) {
  return useApiPost<AssignPatientTariffResponse, AssignPatientTariffRequest>({
    ...options,
  });
}

// Helper to build request payload from selected ids and current date.
// Supports multiple tariff items
export function buildAssignTariffRequest(params: {
  patientId: number;
  items: Array<{ tariffId: number; sourceId: number }>;
}): AssignPatientTariffRequest {
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

  return {
    items: params.items.map(item => ({
      patient: params.patientId,
      tariff: item.tariffId,
      source: item.sourceId,
      assigned_date: today,
      status: 'ACTIVE',
    })),
  };
}

// ---------------------------------------------------------------------------
// Tariff Declaration – Formik helper (simple, like dayThird helper)
// ---------------------------------------------------------------------------

/**
 * Values used by Formik for the small "Tariff Declaration" form.
 */
export interface TariffDeclarationFormValues {
  tariffId: number | '';
  sourceId: number | '';
}

/**
 * Yup validation for Tariff Declaration.
 * Both fields are required numbers.
 */
export const TariffDeclarationValidationSchema = Yup.object().shape({
  tariffId: Yup.number()
    .typeError('Tariff is required')
    .required('Tariff is required'),
  sourceId: Yup.number()
    .typeError('Source is required')
    .required('Source is required'),
});
